import { RASA, type Rasa } from '@iqrapath/shared'
import { ChevronLeft } from 'lucide-react'
import { Link, useSearchParams } from 'react-router-dom'
import { EmptyState } from '../../components/EmptyState.tsx'
import { Obj } from '../../components/Obj.tsx'
import { RASA_INFO, tintOf, usePeganganList, type PeganganItem } from './pegangan.ts'

function MiniCard({ it, today = false }: { it: PeganganItem; today?: boolean }) {
  return (
    <Link className={`pg-mini reveal${today ? ' today' : ''}`} to={`/pegangan/${it.id}`} style={{ background: tintOf(it.rasa) }}>
      <span className="pg-mini-t">
        {today && <span className="eyebrow">Pegangan hari ini</span>}
        <b>{it.judul}</b>{' '}
        <small>{it.ringkas}</small>{' '}
        <span className="pg-ref">{it.ref}</span>
      </span>
      <Obj id={RASA_INFO[it.rasa].obj} size={today ? 72 : 56} />
    </Link>
  )
}

/** /pegangan?rasa=: pick a feeling → situation cards (docs/UX-PLAYBOOK.md §7.4, PRD US-6.1). */
export function PeganganHome() {
  const [params] = useSearchParams()
  const q = params.get('rasa')
  const rasa = (RASA as readonly string[]).includes(q ?? '') ? (q as Rasa) : undefined
  const { data, isPending, isError, refetch } = usePeganganList(rasa)
  const today = data?.today ? data.items.find((i) => i.id === data.today) : undefined

  return (
    <div className="page pg-home">
      <Link className="back" to="/"><ChevronLeft size={18} aria-hidden="true" /> Beranda</Link>
      <h1>Pegangan</h1>
      <p className="muted" style={{ marginTop: 6 }}>Kartu ayat untuk saat hati sedang berat. Pilih yang paling dekat dengan perasaanmu.</p>

      <nav className="rasa pg-filter" aria-label="Pilih perasaan">
        <Link to="/pegangan" aria-current={!rasa ? 'page' : undefined}>Semua</Link>
        {RASA.map((r) => (
          <Link key={r} to={`/pegangan?rasa=${r}`} aria-current={rasa === r ? 'page' : undefined}>
            <Obj id={RASA_INFO[r].obj} size={30} />{RASA_INFO[r].label}
          </Link>
        ))}
      </nav>

      {isPending && <div className="stack" aria-busy="true" aria-label="Memuat kartu">{[0, 1, 2].map((i) => <div key={i} className="pg-mini skel sk" />)}</div>}
      {isError && (
        <EmptyState obj="jam-pasir" title="Kartu belum bisa dimuat" action={<button className="btn btn-secondary" onClick={() => refetch()}>Coba lagi</button>}>
          Koneksi lagi bermasalah. Cek internet kamu, lalu coba lagi.
        </EmptyState>
      )}
      {data && data.items.length === 0 && (
        <EmptyState obj="kado" title="Kartu sedang disiapkan">Kartu untuk perasaan ini belum ada. Coba pilih yang lain dulu.</EmptyState>
      )}
      {data && data.items.length > 0 && (
        <>
          {today && <MiniCard it={today} today />}
          {rasa ? (
            <div className="stack" style={{ marginTop: 12 }}>{data.items.map((it) => <MiniCard key={it.id} it={it} />)}</div>
          ) : (
            RASA.filter((r) => data.items.some((i) => i.rasa === r)).map((r) => (
              <section key={r} aria-labelledby={`pg-${r}`}>
                <h2 className="section-t" id={`pg-${r}`}>{RASA_INFO[r].label}</h2>
                <div className="stack">{data.items.filter((i) => i.rasa === r).map((it) => <MiniCard key={it.id} it={it} />)}</div>
              </section>
            ))
          )}
          <p className="help" style={{ marginTop: 18 }}>Bahan renungan, bukan fatwa. Untuk hukum fikih, tanyakan ke ustadz.</p>
        </>
      )}
    </div>
  )
}
